import React, { useState } from "react";

const API_URL = "https://danielcantos.net/api";
const TODAY = new Date().toISOString().split("T")[0];

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

const COLORS = ["#3B82F6","#10B981","#F59E0B","#EF4444","#8B5CF6","#06B6D4","#EC4899","#F97316","#14B8A6","#6366F1"];

export default function AIGanttBuilder({ projectId, onTasksGenerated }) {
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [applying, setApplying] = useState(false);

  const card = {background:"white",borderRadius:12,padding:20,boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"1px solid #E2E8F0"};
  const btn = {background:"#2563EB",color:"white",border:"none",borderRadius:8,padding:"12px 24px",cursor:"pointer",fontWeight:700,fontSize:14};
  const inp = {border:"1px solid #CBD5E1",borderRadius:8,padding:"8px 12px",fontSize:13,width:"100%"};

  const generate = async () => {
    if (!instructions.trim()) return;
    setLoading(true);
    setError("");
    setTasks([]);
    try {
      const res = await fetch(API_URL + "/projects/" + projectId + "/ai-generate-gantt", {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({instructions})
      });
      if (!res.ok) throw new Error("Error del servidor: " + res.status);
      const data = await res.json();
      if (!data.tasks || !Array.isArray(data.tasks) || data.tasks.length === 0) {
        throw new Error("La IA no generó tareas. Intenta con más detalle.");
      }
      // Normalize tasks — fix any bad fields
      const normalized = data.tasks.map((t, i) => ({
        name: String(t.name || "Tarea " + (i+1)),
        description: String(t.description || ""),
        start_date: String(t.start_date || addDays(TODAY, i * 3)),
        end_date: String(t.end_date || addDays(TODAY, i * 3 + (parseInt(t.duration_days) || 3))),
        duration_days: parseInt(t.duration_days) || 3,
        priority: ["high","medium","low"].includes(t.priority) ? t.priority : "medium",
        assigned_to: String(t.assigned_to || ""),
        color: COLORS[i % COLORS.length],
        progress: 0,
        observation: ""
      }));
      setTasks(normalized);
      setStep(2);
    } catch(e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const updateTask = (i, field, val) => {
    const updated = [...tasks];
    updated[i] = {...updated[i], [field]: val};
    // Recalculate duration if dates change
    if (field === "start_date" || field === "end_date") {
      const start = new Date(field === "start_date" ? val : updated[i].start_date);
      const end = new Date(field === "end_date" ? val : updated[i].end_date);
      const diff = Math.max(1, Math.round((end - start) / 86400000));
      updated[i].duration_days = diff;
    }
    if (field === "duration_days") {
      updated[i].end_date = addDays(updated[i].start_date, parseInt(val) || 1);
    }
    setTasks(updated);
  };

  const removeTask = (i) => setTasks(tasks.filter((_,idx) => idx !== i));

  const addManualTask = () => {
    const lastEnd = tasks.length > 0 ? tasks[tasks.length-1].end_date : TODAY;
    setTasks([...tasks, {
      name: "", description: "", start_date: lastEnd,
      end_date: addDays(lastEnd, 3), duration_days: 3,
      priority: "medium", assigned_to: "", color: COLORS[tasks.length % COLORS.length],
      progress: 0, observation: ""
    }]);
  };

  const applyTasks = async () => {
    const valid = tasks.filter(t => t.name.trim());
    if (valid.length === 0) return alert("Agrega al menos una tarea con nombre");
    setApplying(true);
    let ok = 0;
    for (const task of valid) {
      try {
        const r = await fetch(API_URL + "/projects/" + projectId + "/tasks", {
          method: "POST",
          headers: {"Content-Type":"application/json"},
          body: JSON.stringify({
            name: task.name,
            description: (task.description || "") + (task.observation ? "\n📌 " + task.observation : ""),
            start_date: task.start_date + "T00:00:00",
            end_date: task.end_date + "T00:00:00",
            duration_days: task.duration_days,
            priority: task.priority,
            assigned_to: task.assigned_to || "",
            color: task.color,
            progress: 0,
            dependencies: []
          })
        });
        if (r.ok) ok++;
      } catch(e) {}
    }
    setApplying(false);
    setStep(3);
    if (onTasksGenerated) onTasksGenerated();
  };

  const EXAMPLES = [
    "Compra de materia prima 4 días a partir de hoy\nProcesamiento de atún 7 días\nControl de calidad 2 días\nEmpaque y etiquetado 3 días\nDespacho y exportación 2 días",
    "Fase 1: Permisos y diseño, 15 días desde hoy, responsable Juan\nFase 2: Construcción civil, 45 días, responsable Ing. Carlos\nFase 3: Instalación maquinaria, 20 días\nFase 4: Pruebas y certificaciones, 10 días\nFase 5: Arranque operativo, 5 días",
    "Lunes: Reunión inicial con proveedores (1 día)\nRecepción de insumos (3 días)\nProducción batch 1 (5 días)\nInspección sanitaria (2 días)\nEntrega a cliente (1 día)"
  ];

  return (
    <div style={{fontFamily:"Inter,sans-serif"}}>

      {/* STEP 1: Input */}
      {step === 1 && (
        <div>
          <div style={{...card,marginBottom:16,background:"linear-gradient(135deg,#EFF6FF,#F0FDF4)"}}>
            <div style={{display:"flex",gap:12,alignItems:"center"}}>
              <span style={{fontSize:36}}>🤖</span>
              <div>
                <div style={{fontSize:17,fontWeight:800,color:"#1E293B"}}>Constructor de Gantt con IA</div>
                <div style={{fontSize:13,color:"#64748B"}}>Escribe las operaciones con su duración y la IA crea el Gantt completo con fechas automáticas.</div>
              </div>
            </div>
          </div>

          <div style={card}>
            <label style={{fontSize:12,fontWeight:700,color:"#64748B",display:"block",marginBottom:8}}>
              DESCRIBE TUS OPERACIONES — nombre, duración y cualquier detalle
            </label>
            <textarea
              value={instructions}
              onChange={e=>setInstructions(e.target.value)}
              placeholder={"Ejemplo:\nCompra de materia prima 4 días a partir de hoy\nProcesamiento 7 días\nControl de calidad 2 días, responsable María\nEmpaque 3 días\nDespacho 1 día"}
              style={{width:"100%",height:180,border:"1px solid #CBD5E1",borderRadius:8,padding:12,fontSize:13,resize:"vertical",lineHeight:1.7,fontFamily:"Inter,sans-serif",boxSizing:"border-box"}}
            />

            {/* Quick examples */}
            <div style={{marginTop:12,marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",marginBottom:8}}>EJEMPLOS RÁPIDOS — click para usar:</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {["🐟 Proceso de atún","🏗️ Construcción","📦 Operación logística"].map((label,i)=>(
                  <button key={i} onClick={()=>setInstructions(EXAMPLES[i])} style={{background:"#F1F5F9",color:"#475569",border:"1px solid #E2E8F0",borderRadius:20,padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div style={{padding:12,background:"#FEE2E2",borderRadius:8,color:"#DC2626",fontSize:13,marginBottom:12}}>
                ⚠️ {error}
              </div>
            )}

            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <button onClick={generate} disabled={loading||!instructions.trim()} style={{...btn,opacity:loading||!instructions.trim()?0.6:1,flex:1,textAlign:"center"}}>
                {loading ? "⏳ Generando con IA..." : "🚀 Generar Gantt automático"}
              </button>
              <button onClick={()=>{setTasks([{name:"",description:"",start_date:TODAY,end_date:addDays(TODAY,3),duration_days:3,priority:"medium",assigned_to:"",color:COLORS[0],progress:0,observation:""}]);setStep(2);}} style={{...btn,background:"#10B981"}}>
                ✏️ Crear manualmente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: Preview & Edit */}
      {step === 2 && (
        <div>
          <div style={{...card,marginBottom:16,background:"linear-gradient(135deg,#DCFCE7,#EFF6FF)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:17,fontWeight:800,color:"#1E293B"}}>✅ {tasks.length} tareas generadas — revisa y ajusta</div>
                <div style={{fontSize:13,color:"#64748B"}}>Puedes editar fechas, duración, colores y agregar observaciones antes de aplicar.</div>
              </div>
              <button onClick={addManualTask} style={{...btn,background:"#10B981",padding:"8px 16px",fontSize:13}}>+ Agregar tarea</button>
            </div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
            {tasks.map((task, i) => (
              <div key={i} style={{...card,padding:16,border:`2px solid ${task.color}30`}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:10,marginBottom:10}}>
                  {/* Name */}
                  <div style={{gridColumn:"1/-1"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <input type="color" value={task.color} onChange={e=>updateTask(i,"color",e.target.value)} style={{width:28,height:28,border:"none",cursor:"pointer",borderRadius:4}}/>
                      <input style={{...inp,fontWeight:700,fontSize:14,flex:1}} placeholder="Nombre de la operación *" value={task.name} onChange={e=>updateTask(i,"name",e.target.value)}/>
                      <button onClick={()=>removeTask(i)} style={{background:"#FEE2E2",color:"#DC2626",border:"none",borderRadius:6,padding:"6px 10px",cursor:"pointer",fontSize:12,flexShrink:0}}>🗑</button>
                    </div>
                  </div>

                  {/* Dates */}
                  <div>
                    <label style={{fontSize:11,color:"#64748B",fontWeight:600,display:"block",marginBottom:4}}>FECHA INICIO</label>
                    <input type="date" style={inp} value={task.start_date} onChange={e=>updateTask(i,"start_date",e.target.value)}/>
                  </div>
                  <div>
                    <label style={{fontSize:11,color:"#64748B",fontWeight:600,display:"block",marginBottom:4}}>FECHA FIN</label>
                    <input type="date" style={inp} value={task.end_date} onChange={e=>updateTask(i,"end_date",e.target.value)}/>
                  </div>
                  <div>
                    <label style={{fontSize:11,color:"#64748B",fontWeight:600,display:"block",marginBottom:4}}>DURACIÓN (días)</label>
                    <input type="number" min="1" style={{...inp,fontWeight:700,color:"#2563EB"}} value={task.duration_days} onChange={e=>updateTask(i,"duration_days",e.target.value)}/>
                  </div>

                  {/* Priority & Assigned */}
                  <div>
                    <label style={{fontSize:11,color:"#64748B",fontWeight:600,display:"block",marginBottom:4}}>PRIORIDAD</label>
                    <select style={inp} value={task.priority} onChange={e=>updateTask(i,"priority",e.target.value)}>
                      <option value="high">🔴 Alta</option>
                      <option value="medium">🟡 Media</option>
                      <option value="low">🟢 Baja</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11,color:"#64748B",fontWeight:600,display:"block",marginBottom:4}}>RESPONSABLE</label>
                    <input style={inp} placeholder="Nombre o área" value={task.assigned_to} onChange={e=>updateTask(i,"assigned_to",e.target.value)}/>
                  </div>

                  {/* Observation */}
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:11,color:"#64748B",fontWeight:600,display:"block",marginBottom:4}}>📌 OBSERVACIÓN (opcional)</label>
                    <input style={{...inp,background:"#FFFBEB"}} placeholder="Notas, condiciones especiales, materiales necesarios..." value={task.observation||""} onChange={e=>updateTask(i,"observation",e.target.value)}/>
                  </div>
                </div>

                {/* Mini timeline preview */}
                <div style={{background:"#F8FAFC",borderRadius:6,padding:"6px 10px",display:"flex",alignItems:"center",gap:8,fontSize:11,color:"#64748B"}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:task.color}}/>
                  <span>📅 {task.start_date}</span>
                  <span>→</span>
                  <span>📅 {task.end_date}</span>
                  <span style={{fontWeight:700,color:"#2563EB",marginLeft:4}}>({task.duration_days} días)</span>
                  {task.assigned_to && <span style={{marginLeft:8}}>👤 {task.assigned_to}</span>}
                </div>
              </div>
            ))}
          </div>

          <div style={{display:"flex",gap:8}}>
            <button onClick={applyTasks} disabled={applying} style={{...btn,flex:1,padding:16,fontSize:15,opacity:applying?0.6:1}}>
              {applying ? "⏳ Creando tareas..." : "✅ Aplicar al Gantt — Crear " + tasks.filter(t=>t.name.trim()).length + " tareas"}
            </button>
            <button onClick={()=>setStep(1)} style={{background:"#F1F5F9",color:"#475569",border:"none",borderRadius:8,padding:"16px 24px",cursor:"pointer",fontWeight:600}}>
              ← Volver
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Done */}
      {step === 3 && (
        <div style={{...card,textAlign:"center",padding:60}}>
          <div style={{fontSize:64,marginBottom:16}}>🎉</div>
          <div style={{fontSize:22,fontWeight:800,color:"#1E293B",marginBottom:8}}>¡Gantt creado exitosamente!</div>
          <div style={{fontSize:14,color:"#64748B",marginBottom:24}}>Las tareas se agregaron al diagrama con fechas y colores automáticos.</div>
          <div style={{display:"flex",gap:12,justifyContent:"center"}}>
            <button onClick={()=>{setStep(1);setInstructions("");setTasks([]);setError("");}} style={{...btn,background:"#F1F5F9",color:"#475569"}}>
              Generar otro plan
            </button>
            <button onClick={onTasksGenerated} style={btn}>
              📊 Ver Gantt →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
